import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, SectionHead } from "@/components/Reveal";
import { CONDITION_GROUPS, FEATURED_CONDITIONS } from "@/lib/site";

export default function Conditions() {
  return (
    <div data-testid="conditions-page" className="pt-[72px]">
      <Seo
        title="Conditions Treated | Kamana Physiotherapy & Rehab Center, Lucknow"
        description="Back pain, sciatica, frozen shoulder, arthritis, stroke, Bell's palsy, sports injuries and post-surgical rehab — conditions treated at Kamana Physiotherapy, Gomti Nagar, Lucknow."
      />

      <section className="bg-teal-obsidian text-alabaster grain-dark relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <p className="mono-label text-gold">Conditions Treated</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mt-4 leading-[1.06] max-w-3xl">
            Whatever it's called, it has <span className="italic text-gold">a way out.</span>
          </h1>
          <p className="mt-6 text-alabaster/70 max-w-2xl leading-relaxed">
            From a stubborn tennis elbow to full post-stroke rehabilitation — these are the
            conditions our Gomti Nagar clinic treats every single week.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
        <SectionHead label="Common Conditions" title="Pain Has Many Names.\nRecovery Has One Address." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {FEATURED_CONDITIONS.map((c, i) => (
            <Reveal key={c.name} delay={i * 0.06}>
              <div className="h-full bg-white border border-teal-brand/10 rounded-3xl p-7 card-lift">
                <span className="mono-label text-gold-deep !text-[9px]">0{i + 1}</span>
                <h3 className="font-display text-xl font-semibold text-teal-brand mt-3 leading-snug">{c.name}</h3>
                <p className="text-sm text-inkmuted leading-relaxed mt-3">{c.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-teal-soft/60 border-y border-teal-brand/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
          <SectionHead label="Body Map" title="Find Your Concern\nby Region" />
          <div className="grid sm:grid-cols-2 gap-5 mt-12">
            {CONDITION_GROUPS.map((g, i) => (
              <Reveal key={g.title} delay={i * 0.08}>
                <div className="h-full bg-white border border-teal-brand/10 rounded-3xl p-8 card-lift">
                  <h3 className="font-display text-2xl font-semibold text-teal-brand">{g.title}</h3>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {g.conditions.map((c) => (
                      <span
                        key={c}
                        className="text-xs font-semibold bg-teal-soft text-teal-brand border border-teal-brand/15 rounded-full px-4 py-2"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12 text-center">
              <Link
                to="/book-appointment"
                data-testid="conditions-book-cta"
                className="group inline-flex items-center gap-2 bg-teal-brand text-alabaster font-semibold rounded-full px-8 py-4 text-sm hover:bg-teal-deep transition-colors duration-300"
              >
                Get Your Condition Assessed
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
